<script setup lang="ts">
import './assets/style.scss'
import { useLoadInventMapTypes } from './../model/api.ts'
import type { InventMapObjectTypeRow, InventMapSubtypeRow } from './../model/unwrapFullTypesResponse.ts'
import { unwrapFullTypesList } from './../model/unwrapFullTypesResponse.ts'
import { mapSubtypeToPreviewType } from '@/features/Inventory/PlaceOnMap/model/resolvePlacePreviewType.ts'
import type { InventoryMapObjectTypes } from '@/widgets/InventoryMap/model/types.ts'
import { useRoute } from 'vue-router'
import { computed, mergeProps } from 'vue'
import { VIcons } from '@/shared/ui'
import { useAuth } from '@/entities/Auth'
import { useTranslate } from '@/shared/lib'

const props = defineProps({
  disabled: {
    type: Boolean,
    default: false,
  },
})

const { isPermissions, hasElementPermission } = useAuth()
const { trs } = useTranslate()
const route = useRoute()
const objectId = computed(() => String(route.params.object_id))

const emits = defineEmits(['selected'])

const { data, isPending, isError } = useLoadInventMapTypes(objectId)

const addTooltip = computed(() =>
  props.disabled ? trs('itf.up_zoom_to_activate') : trs('itf.add_object'),
)

/* Вычисляем список элементов для добавления */
const items = computed(() => {
  const rows = unwrapFullTypesList(data.value)
  return rows
    .filter((item) => Array.isArray(item.sub_types) && item.sub_types.length > 0)
    .filter((item) => hasElementPermission(item.id, 'create'))
})

/**
 * Нормализует subtype из API (figure/line могут быть null) в полный InventoryMapObjectTypes.
 */
const clickHandler = (parent: InventMapObjectTypeRow, subItem: InventMapSubtypeRow) => {
  emits('selected', mapSubtypeToPreviewType(parent, subItem) as InventoryMapObjectTypes)
}


/** Элемент имеет ровно один подтип — подменю не показываем, клик по пункту выбирает его */
const hasSingleSubType = (item) => item.sub_types?.length === 1
</script>

<template>
  <VMenu>
    <template #activator="{ props: menuProps }">
      <VTooltip location="top" :open-delay="500" :text="addTooltip">
        <template #activator="{ props: tooltipProps }">
          <VBtn
            color="warning"
            :disabled="isPending || disabled || !isPermissions(['OBJECT_EDIT'])"
            value="add"
            v-bind="mergeProps(menuProps, tooltipProps)"
          >
            <span class="hide_mobile">{{ $t('itf.add_object') }}</span>

            <template #append>
              <VIcons type="arrow_drop_down" />
            </template>

            <template #prepend>
              <VIcons type="add_circle" />
            </template>
          </VBtn>
        </template>
      </VTooltip>
    </template>

    <VList>
      <VListItem v-if="isError" disabled>
        <VListItemTitle class="text-error">{{ trs('request.unknown_error') }}</VListItemTitle>
      </VListItem>
      <VListItem v-else-if="!isPending && !items.length" disabled>
        <VListItemTitle>{{ trs('itf.map_add_types_empty') }}</VListItemTitle>
      </VListItem>
      <VListItem
        v-for="item in items"
        :key="item.id"
        :class="{ 'invent-map-types__item_single': hasSingleSubType(item) }"
        @click="hasSingleSubType(item) ? clickHandler(item, item.sub_types[0]) : undefined"
      >
        <template #title>
          <div class="invent-map-types__menu-item">
            <VIcons :type="item.icon" /> {{ item.object_name }}
          </div>
        </template>

        <template v-if="!hasSingleSubType(item)" #append>
          <VIcons type="arrow_right" />
        </template>

        <VMenu v-if="!hasSingleSubType(item)" activator="parent" open-on-hover submenu>
          <VList>
            <VListItem
              v-for="subItem in item.sub_types"
              :key="subItem.id"
              :title="subItem.name"
              @click="clickHandler(item, subItem)"
            />
          </VList>
        </VMenu>
      </VListItem>
    </VList>
  </VMenu>
</template>
